import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Alert, LoadingState } from '@types'
import { alertsApi } from '@api/alerts'

interface AlertsState {
  alerts: Alert[]
  selectedAlerts: number[]
  loadingState: LoadingState
  error: string | null
  search: string
}

const initialState: AlertsState = {
  alerts: [],
  selectedAlerts: [],
  loadingState: 'idle',
  error: null,
  search: '',
}

// Async thunks
export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await alertsApi.getAlerts()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch alerts')
    }
  }
)

export const deleteAlerts = createAsyncThunk(
  'alerts/deleteAlerts',
  async (alertIds: number[], { rejectWithValue }) => {
    try {
      await alertsApi.deleteAlerts(alertIds)
      return alertIds
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete alerts')
    }
  }
)

export const markAlertsAsRead = createAsyncThunk(
  'alerts/markAlertsAsRead',
  async (alertIds: number[], { rejectWithValue }) => {
    try {
      await alertsApi.markAlertsAsRead(alertIds)
      return alertIds
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark alerts as read')
    }
  }
)

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    setSelectedAlerts: (state, action: PayloadAction<number[]>) => {
      state.selectedAlerts = action.payload
    },
    toggleAlertSelection: (state, action: PayloadAction<number>) => {
      const alertId = action.payload
      const index = state.selectedAlerts.indexOf(alertId)
      if (index > -1) {
        state.selectedAlerts.splice(index, 1)
      } else {
        state.selectedAlerts.push(alertId)
      }
    },
    selectAllAlerts: (state) => {
      state.selectedAlerts = state.alerts.map(alert => alert.id)
    },
    clearSelectedAlerts: (state) => {
      state.selectedAlerts = []
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Alerts
      .addCase(fetchAlerts.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.alerts = Array.isArray(action.payload?.data) ? action.payload.data : []
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Delete Alerts
      .addCase(deleteAlerts.fulfilled, (state, action) => {
        state.alerts = state.alerts.filter(alert => !action.payload.includes(alert.id))
        state.selectedAlerts = []
      })

      // Mark Alerts as Read
      .addCase(markAlertsAsRead.fulfilled, (state, action) => {
        action.payload.forEach(alertId => {
          const alert = state.alerts.find(a => a.id === alertId)
          if (alert) {
            alert.acknowledged = true
          }
        })
      })
  },
})

export const {
  setSelectedAlerts,
  toggleAlertSelection,
  selectAllAlerts,
  clearSelectedAlerts,
  setSearch,
  clearError,
  resetState,
} = alertsSlice.actions

export default alertsSlice.reducer
